/**
 * OPAC 統計の派生値計算とソートランク
 *
 * adapter によっては欠けた値があるため、他フィールドから補完する。
 * (例: LICS-Re は holdings を返さないが loanable + on_loan で計算可能)
 */

import type { LibraryStatsResult } from '@/api/types/library-stats'

export type DerivedStats = {
  /** 総予約数 (確保+待ち合算) */
  reservations: number | null
  /** 所蔵冊数 */
  holdings: number | null
  /** 貸出可能冊数 (今すぐ借りられる分) */
  loanable: number | null
  /** 貸出中冊数 */
  on_loan: number | null
  /**
   * 巡回数 (自分の予約が入った場合、何巡目で借りられるか)。
   * `ceil(reservations / holdings)`。holdings が不明・0 なら null。
   * 予約 0 のときは 0 (今すぐ借りられる可能性)。
   */
  turns: number | null
}

/**
 * 生の統計から派生値を計算する。取得失敗・未対応・null は null を返す。
 */
export const deriveStats = (stats: LibraryStatsResult | undefined): DerivedStats | null => {
  if (!stats || stats.status !== 'ok') return null

  const reservations = stats.reservations
  let holdings = stats.holdings
  let loanable = stats.loanable
  const onLoan = stats.on_loan

  // holdings を loanable + on_loan から補完
  if (holdings == null && loanable != null && onLoan != null) {
    holdings = loanable + onLoan
  }
  // loanable を holdings - on_loan から補完
  if (loanable == null && holdings != null && onLoan != null) {
    loanable = holdings - onLoan
  }

  let turns: number | null = null
  if (reservations != null && holdings != null && holdings > 0) {
    turns = Math.ceil(reservations / holdings)
  } else if (reservations === 0 && holdings != null) {
    // holdings=0 でも予約 0 なら 0 巡
    turns = 0
  }

  return {
    reservations,
    holdings,
    loanable,
    on_loan: onLoan,
    turns,
  }
}

/**
 * 貸出可否のカテゴリ。表示色・ソート優先度・ラベルを分岐する軸。
 *
 * - `available`: 貸出可能な冊がある (`loanable >= 1`) — 今すぐ借りられる
 * - `soon`: 全部貸出中だが予約 0 (`loanable === 0 && reservations === 0`) —
 *   返却され次第 (誰でも) 借りられる
 * - `waiting`: 予約待ちあり (`reservations >= 1`) — `turns` 分待つ必要がある
 * - `unknown`: 上記いずれも判定不能 (取得失敗 / adapter 未対応 / `loanable` 不明など)
 */
export type Availability = 'available' | 'soon' | 'waiting' | 'unknown'

/**
 * 派生統計から貸出可否カテゴリを算出する。
 *
 * `loanable` が null (adapter が返せなかった) 場合は `available`/`soon` を
 * 区別できないため `unknown` を返す (誤った緑表示を避ける)。
 */
export const deriveAvailability = (derived: DerivedStats | null): Availability => {
  if (!derived) return 'unknown'
  if (derived.loanable != null && derived.loanable > 0) return 'available'
  const reservations = derived.reservations
  if (derived.loanable === 0 && reservations === 0) return 'soon'
  if (reservations != null && reservations > 0) return 'waiting'
  return 'unknown'
}

/**
 * ソート優先度バケット。
 *
 * 0: available (今すぐ借りられる)
 * 1: soon (全部貸出中だが予約 0)
 * 2: waiting (巡回数少ない順)
 * 3: 統計フェッチ中
 * 4: unknown / 未対応 / エラー
 */
export type SortBucket = 0 | 1 | 2 | 3 | 4

export type EntryRank = {
  bucket: SortBucket
  /** バケット内での 2 次キー (巡回数など) */
  key: number
}

/**
 * 所蔵館エントリーのソートランクを算出する。
 */
export const rankEntry = (derived: DerivedStats | null, isFetching: boolean): EntryRank => {
  const availability = deriveAvailability(derived)
  if (availability === 'available') return { bucket: 0, key: 0 }
  if (availability === 'soon') return { bucket: 1, key: 0 }
  if (availability === 'waiting') {
    // waiting なら turns を 2 次キーに (未計算なら大きめの値で末尾に寄せる)
    const turns = derived?.turns ?? Number.MAX_SAFE_INTEGER
    return { bucket: 2, key: turns }
  }
  if (isFetching) return { bucket: 3, key: 0 }
  return { bucket: 4, key: 0 }
}
